import { ONBOARDING_STEPS } from "types/onboarding";
import { UploadProduct } from "types/products";

type StepInfo = {
  emoji: string;
  description: string;
  action: string;
};

type Survey = {
  companyName?: string;
  tenant?: string;
  goals?: string | string[];
  referralSource?: string;
  marketplace?: string;
};

const stepDetails = {
  [ONBOARDING_STEPS.initializing]: {
    emoji: "🆕",
    description: "Account created",
    action: "New user has been registered",
  },
  [ONBOARDING_STEPS.survey]: {
    emoji: "📋",
    description: "Onboarding survey submitted",
    action: "Additional user information collected",
  },
  [ONBOARDING_STEPS.catalog]: {
    emoji: "📦",
    description: "Product catalog uploaded",
    action: "User has uploaded their product catalog",
  },
  [ONBOARDING_STEPS.finalizing]: {
    emoji: "✅",
    description: "Successfully logged into portal",
    action: "User has completed onboarding and is now active",
  },
  [ONBOARDING_STEPS.upgrade]: {
    emoji: "🚨",
    description: "Customer requested upgrade",
    action: "User has expressed interest in upgrading their plan",
  },
};

const buildSurveySection = (survey: Survey | null): string => {
  if (!survey) return "";

  const goalsText = Array.isArray(survey.goals)
    ? survey.goals.join(", ")
    : survey.goals || "Not specified";

  return `
    <div style="margin-top: 20px; padding: 15px; background-color: #f0f8ff; border-radius: 5px; border-left: 4px solid #4CAF50;">
      <h3 style="margin-top: 0; color: #2c3e50; font-size: 16px;">📋 Survey Responses</h3>
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 8px 0; font-weight: bold; width: 40%;">Company Name:</td>
          <td style="padding: 8px 0;">${survey.companyName || "N/A"}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; font-weight: bold;">Tenant ID:</td>
          <td style="padding: 8px 0;">${survey.tenant || "N/A"}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; font-weight: bold;">Goals:</td>
          <td style="padding: 8px 0;">${goalsText}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; font-weight: bold;">Referral Source:</td>
          <td style="padding: 8px 0;">${survey.referralSource || "N/A"}</td>
        </tr>
        <tr>
          <td style="padding: 8px 0; font-weight: bold;">Target Marketplace:</td>
          <td style="padding: 8px 0;">${survey.marketplace || "N/A"}</td>
        </tr>
      </table>
    </div>
  `;
};

const buildCatalogSection = (catalog: UploadProduct[] | null): string => {
  if (!catalog || !Array.isArray(catalog) || catalog.length === 0) return "";

  const productRows = catalog
    .slice(0, 10)
    .map(
      (product) => `
      <tr style="border-bottom: 1px solid #e0e0e0;">
        <td style="padding: 8px;">${product.name || "N/A"}</td>
        <td style="padding: 8px;">${product.brand || "N/A"}</td>
        <td style="padding: 8px;">${product.amazonId || "N/A"}</td>
        <td style="padding: 8px;">${product.sku || "N/A"}</td>
        <td style="padding: 8px;">${
          product.map ? `$${product.map}` : "N/A"
        }</td>
      </tr>
    `
    )
    .join("");

  const additionalCount = catalog.length > 10 ? catalog.length - 10 : 0;

  return `
    <div style="margin-top: 20px; padding: 15px; background-color: #fff8e1; border-radius: 5px; border-left: 4px solid #FF9800;">
      <h3 style="margin-top: 0; color: #2c3e50; font-size: 16px;">📦 Product Catalog (${
        catalog.length
      } total products)</h3>
      <div style="overflow-x: auto;">
        <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
          <thead>
            <tr style="background-color: #f5f5f5; border-bottom: 2px solid #ddd;">
              <th style="padding: 10px; text-align: left; font-weight: 600;">Product Name</th>
              <th style="padding: 10px; text-align: left; font-weight: 600;">Brand</th>
              <th style="padding: 10px; text-align: left; font-weight: 600;">ASIN</th>
              <th style="padding: 10px; text-align: left; font-weight: 600;">SKU</th>
              <th style="padding: 10px; text-align: left; font-weight: 600;">MAP</th>
            </tr>
          </thead>
          <tbody>
            ${productRows}
          </tbody>
        </table>
      </div>
      ${
        additionalCount > 0
          ? `<p style="margin-top: 10px; font-style: italic; color: #666;">... and ${additionalCount} more product${
              additionalCount > 1 ? "s" : ""
            }</p>`
          : ""
      }
    </div>
  `;
};

const buildNextSteps = (
  step: (typeof ONBOARDING_STEPS)[keyof typeof ONBOARDING_STEPS],
  survey: Survey | null,
  catalog: UploadProduct[] | null
): string => {
  let nextSteps = "";

  if (step === ONBOARDING_STEPS.initializing) {
    nextSteps = `
      <li>Monitor for survey completion</li>
      <li>Consider outreach</li>
    `;
  } else if (step === ONBOARDING_STEPS.survey) {
    nextSteps = `
      <li>Review customer requirements and use case</li>
      <li>Note their marketplace focus: <strong>${
        survey?.marketplace || "Not specified"
      }</strong></li>
    `;
  } else if (step === ONBOARDING_STEPS.catalog) {
    nextSteps = `
      <li>Review catalog for quality and completeness (${
        catalog?.length || 0
      } products)</li>
      <li>Verify ASIN accuracy and brand information</li>
      <li>Reach out to assist with setup if needed</li>
    `;
  } else if (step === ONBOARDING_STEPS.finalizing) {
    nextSteps = `
      <li>Ensure customer is successfully using the portal</li>
      <li>Provide onboarding resources and training materials</li>
    `;
  }

  if (!nextSteps) return "";

  return `
    <div style="margin-top: 20px; padding: 15px; background-color: #fff; border: 1px solid #ddd; border-radius: 5px;">
      <p style="margin-top: 0;"><strong>Next Steps for Sales/Support:</strong></p>
      <ul style="line-height: 1.8; margin-bottom: 0;">
        ${nextSteps}
      </ul>
    </div>
  `;
};

const buildMainBody = (
  step: (typeof ONBOARDING_STEPS)[keyof typeof ONBOARDING_STEPS],
  stepInfo: StepInfo,
  customerName: string,
  email: string,
  survey: Survey | null = null,
  catalog: UploadProduct[] | null = null
): string => {
  const surveySection = buildSurveySection(survey);
  const catalogSection = buildCatalogSection(catalog);
  const nextStepsSection = buildNextSteps(step, survey, catalog);

  return `
    <div style="font-family: Arial, sans-serif; max-width: 700px;">
      <h2 style="color: #2c3e50; border-bottom: 2px solid #3498db; padding-bottom: 10px;">
        ${stepInfo.emoji} Self-Serve Onboarding Alert
      </h2>
      
      <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
        <p style="margin: 5px 0;"><strong>Customer:</strong> ${customerName}</p>
        <p style="margin: 5px 0;"><strong>Email:</strong> ${email}</p>
        <p style="margin: 5px 0;"><strong>Step:</strong> ${
          stepInfo.description
        }</p>
        <p style="margin: 5px 0;"><strong>Timestamp:</strong> ${new Date().toLocaleString()}</p>
      </div>
      
      <div style="padding: 15px; background-color: #e8f4f8; border-left: 4px solid #3498db; margin: 20px 0;">
        <p style="margin: 0;"><strong>Status:</strong> ${stepInfo.action}</p>
      </div>

      ${surveySection}
      ${catalogSection}
      ${nextStepsSection}
      
      <hr style="border: none; border-top: 1px solid #ddd; margin: 30px 0;" />
      
      <p style="font-size: 12px; color: #7f8c8d;">
        This is an automated notification from the Sigil Portal self-serve onboarding system.
      </p>
    </div>
  `;
};

export { buildMainBody, stepDetails, type StepInfo, type Survey };
